import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import CloseIcon from "components/CloseIcon/CloseIcon";
import { useProfile } from "contexts/ProfileContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getHistoricoViagens } from "services/ModuloGerenciador/DisponibilidadeFrota";
import { formatDateTime } from "./ModalHistoricoUtils";
import "./ModalHistoricoViagensFrota.css";

type Props = {
	open: boolean;
	onClose: () => void;
	token: string | undefined;
	cteId: number;
	onError?: (msg: string) => void;
	titulo?: string;
};

export interface HistoricoFrota {
	cte: number;
	motoristaSelecionado: { nome: string };
	unidEmbarque: string;
	filialEmbarque: { nome: string };
	pedidoEmbarque: {
		pedido: string,
		rota: {
			cidadeDestinoRota: {
				abreviacao: string,
				estado: string
			},
			cidadeOrigemRota: {
				abreviacao: string,
				estado: string
			},
		}
	};
	load: string;
	dataHoraEmbarcado: string;
}

type Column<Row> = {
	key: string;
	header: string;
	width?: number;
	align?: "left" | "right" | "center";
	nowrap?: boolean;
	render: (row: Row) => React.ReactNode;
};

export default function ModalHistoricoTabela(props: Props) {
	const { handleSetContextoEmbarque } = useProfile();
	const { open, onClose, token, cteId, titulo } = props;

	const [loading, setLoading] = useState(false);
	const [items, setItems] = useState<HistoricoFrota[]>([]);

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const resp = await getHistoricoViagens({ token, id: cteId });
			setItems(resp);
		} catch (error: any) {
			setItems([]);
		} finally {
			setLoading(false);
		}
	}, [cteId, token]);

	useEffect(() => {
		if (!open) return;
		fetchData();
	}, [open, fetchData]);

	const columns = useMemo<Column<HistoricoFrota>[]>(() => {
		return [
			{
				key: "cte",
				header: "CTE",
				width: 90,
				align: "left",
				nowrap: true,
				render: (r) => r.cte,
			},
			{
				key: "unidEmbarque",
				header: "Unidade Embarque",
				width: 140,
				align: "left",
				nowrap: true,
				render: (r) => r.unidEmbarque,
			},
			{
				key: "filial",
				header: "Filial",
				width: 120,
				align: "left",
				nowrap: true,
				render: (r) => r.filialEmbarque?.nome ?? "-",
			},
			{
				key: "rota",
				header: "Rota",
				width: 180,
				align: "left",
				nowrap: true,
				render: (r) => `${r.pedidoEmbarque?.rota?.cidadeOrigemRota?.abreviacao ?? "-"} - ${r.pedidoEmbarque?.rota?.cidadeOrigemRota?.estado ?? "-"}
					X ${r.pedidoEmbarque?.rota?.cidadeDestinoRota?.abreviacao ?? "-"} - ${r.pedidoEmbarque?.rota?.cidadeDestinoRota?.estado ?? "-"}`,
			},
			{
				key: "dataEmbarque",
				header: "Data Embarque",
				width: 140,
				align: "left",
				nowrap: true,
				render: (r) => {
					if (!r.dataHoraEmbarcado) return "-";

					const formatted = formatDateTime(r.dataHoraEmbarcado);

					if (formatted?.startsWith("01/01/2000")) return "-";

					return formatted;
				},
			}

		];
	}, [formatDateTime]);

	const navigate = useNavigate();
	const navegarEdicaoEmbarque = useCallback(
		(row) => {
			handleSetContextoEmbarque(row.contexto);
			navigate(`/viagens/${row.contexto}/cadastro/${row.idEmbarque}`);
		},
		[navigate, handleSetContextoEmbarque]
	);

	const body = useMemo(() => {
		if (loading) {
			return (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<CircularProgress size={28} />
				</div>
			);
		}

		const cellBase: React.CSSProperties = { borderBottom: "1px solid #eee" };
		const thBase: React.CSSProperties = {
			...cellBase,
			fontWeight: 600,
			position: "sticky",
			top: 0,
			background: "#fff",
			zIndex: 1,
			borderBottom: "1px solid #ddd",
			whiteSpace: "nowrap",
		};

		return (
			<Paper elevation={0}>
				<div style={{ marginBottom: 35, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<span>
						<b>Motorista:</b> {items[0]?.motoristaSelecionado?.nome ?? "-"}
					</span>
					<CloseIcon
						onClick={() => {
							onClose();
						}}
					/>
				</div>

				<div style={{ maxHeight: 280, overflow: "auto" }}>
					<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
						<thead>
							<tr>
								{columns.map((col) => (
									<th
										key={col.key}
										style={{
											...thBase,
											width: col.width,
											textAlign: col.align ?? "left",
										}}
									>
										{col.header}
									</th>
								))}
							</tr>
						</thead>

						<tbody>
							{items.map((row, idx) => (
								<tr
									key={`${row.cte}-${idx}`}
									className="historico-row"
									onDoubleClick={() => {
										navegarEdicaoEmbarque(row);
									}}
								>
									{columns.map((col) => (
										<td
											key={col.key}
											style={{
												...cellBase,
												width: col.width,
												textAlign: col.align ?? "left",
												whiteSpace: col.nowrap ? "nowrap" : undefined,
											}}
										>
											{col.render(row)}
										</td>
									))}
								</tr>
							))}

							{!loading && items.length === 0 && (
								<tr>
									<td colSpan={columns.length} style={{ padding: 18, textAlign: "center" }}>
										Nenhum registro encontrado.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</Paper>
		);
	}, [columns, items, loading]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm">
			<DialogTitle>{titulo ?? "Motorista"}</DialogTitle>
			<DialogContent>{body}</DialogContent>
		</Dialog>
	);
}
